import { isEqual } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import XLSX from 'xlsx';
import { Button } from '@praktio/common-ui';
import { Container, Col, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';

import { RootState } from '../../ducks/store';
import { getTeams } from '../../ducks/teams';
import { bulkEditUsers } from '../../ducks/users';
import { buildBulkTableData } from '../../utils/functions';
import ActionBar from '../common/ActionBar';
import { Table } from '../common/Table/Table';
import { TeamSize, BulkEditHeaders, CLESize } from '../../utils/constants';

import './BulkEditUsers.scss';

const BulkEditUsers = () => {
  const dispatch = useDispatch();
  const { teams } = useSelector((state: RootState) => {
    const teams = state.teams.list;
    return { teams };
  });

  useEffect(() => {
    dispatch(getTeams());
  }, [dispatch]);

  const [tableData, setTableData] = useState<any[]>([]);
  const [allValid, setAllValid] = useState(false);
  const prevTableData = useRef<any[]>([]);

  const onSaveClick = () => {
    const users = tableData.map((d) => {
      const {
        email,
        firstName,
        lastName,
        password,
        practiceGroup,
        firmOffice,
        currentPosition,
        noCle,
        clearCle,
        updateEmail,
      } = d;

      const user: IUser = {
        email,
        firstName,
        lastName,
        password,
        practiceGroup,
        firmOffice,
        position: currentPosition,
        teams: [],
        cles: [],
        clearCle,
        updateEmail,
      };

      for (let i = 1; i <= TeamSize; i++) {
        const teamId = d[`team${i}id`];
        if (teamId) {
          user.teams?.push({ _id: teamId } as any);
        }
      }

      for (let i = 1; i <= CLESize; i++) {
        const state = d[`cleState${i}`];
        const barNumber = d[`cleBarNumber${i}`];
        if (state || barNumber) {
          user.cles?.push({ state, barNumber });
        }
      }

      // Prevents opting out of CLEs if clearCle is true
      if (noCle && !clearCle) {
        user.optOutCLEs = true;
      }

      return user;
    });

    if (users.length > 0) {
      dispatch(bulkEditUsers(users));
    }
  };

  const hasChanges = tableData && tableData.length && !isEqual(tableData, prevTableData.current);
  return (
    <Container fluid className="BulkEditUsers">
      <Helmet>
        <title>Bulk Edit Users - Praktio</title>
      </Helmet>
      <ActionBar flex>
        <Button disabled={!allValid || !hasChanges} btnStyle="primary" onClick={onSaveClick}>
          SAVE
        </Button>
        <Button
          className="secondary"
          onClick={() => {
            const ws = XLSX.utils.aoa_to_sheet([BulkEditHeaders.map((h) => h.Header)]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'BulkAssignment');
            XLSX.writeFile(wb, 'BulkEditTemplate.xlsx');
          }}
        >
          DOWNLOAD TEMPLATE
        </Button>
        <Button
          className="secondary"
          type="file"
          accept=".xlsx"
          onFileSelect={(e) => {
            if (e?.target.files && e.target.files.length > 0) {
              const file = e?.target.files[0];
              if (file) {
                const fileReader = new FileReader();
                fileReader.onload = (e) => {
                  if (e?.target?.result) {
                    const results = buildBulkTableData(
                      BulkEditHeaders,
                      e.target.result as ArrayBuffer,
                      teams,
                    );

                    setTableData(results.data);
                    setAllValid(results.valid);
                  }
                };

                fileReader.readAsArrayBuffer(file as unknown as Blob);
              }
            }
          }}
        >
          IMPORT EDITS
        </Button>
      </ActionBar>
      <Row>
        <Col xs={3} md={3}>
          <Table
            tableTitle="Teams"
            columns={[
              { Header: 'Team', accessor: 'name' },
              { Header: 'ID', accessor: '_id', isId: true },
            ]}
            data={teams || []}
          />
        </Col>
        <Col xs={9} md={9}>
          <Table tableTitle="Edit Users" columns={BulkEditHeaders} data={tableData} noPaging />
        </Col>
      </Row>
    </Container>
  );
};

export default BulkEditUsers;
